import { ArrowPathIcon } from "@heroicons/react/24/solid";
import { t } from "@lingui/core/macro";
import { captureException } from "@sentry/react";
import { useEffect, useRef } from "react";
import { useSessionStorage } from "usehooks-ts";

import { Button } from "@/shared/components/ui/button";
import { Card } from "@/shared/components/ui/card";

const MAX_REFRESH_ATTEMPTS = 3;

export default function StaleContentFallback({ error }: { error: unknown }) {
  const [refreshAttempts, setRefreshAttempts] = useSessionStorage("stale-content-fallback-refresh-attempts", 0);
  const maxAttemptsReached = refreshAttempts >= MAX_REFRESH_ATTEMPTS;
  const hasAttemptedRefreshThisLoad = useRef(false);

  // Attempt one auto-reload per page load. Incrementing refreshAttempts must not
  // re-run this effect, or every retry would be consumed before location.reload() finishes.
  useEffect(() => {
    if (hasAttemptedRefreshThisLoad.current || maxAttemptsReached) {
      return;
    }
    hasAttemptedRefreshThisLoad.current = true;
    setRefreshAttempts((attempts) => attempts + 1);
    window.location.reload();
  }, [maxAttemptsReached, setRefreshAttempts]);

  useEffect(() => {
    if (maxAttemptsReached) {
      // only log the error to sentry if we reached the max attempts
      captureException(new Error("Stale content fallback reached max attempts"), {
        originalException: error,
      });
    }
  }, [maxAttemptsReached, error]);

  if (!maxAttemptsReached) return null;

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-200">
      <div className="container max-w-xl">
        <Card className="flex flex-col items-center gap-4 p-8">
          <ArrowPathIcon className="h-9 w-auto text-muted-foreground" />
          <h1 className="text-center text-xl">{t`We Have Updated Our Website`}</h1>
          <p className="text-center [textWrap:balance]">{t`Refresh your page to continue your session and see the most up to date content.`}</p>
          <Button className="w-full" onClick={() => window.location.reload()}>
            {t`Refresh`}
          </Button>
        </Card>
      </div>
    </div>
  );
}
